import { useEffect, useRef, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { EndpointDraft, parseRoutePoint } from "./route-preview";
import type { OrderLocation } from "./InfoPopup";

export default function RouteEndpointEditor({ label, value, fallback, orders, onChange }: {
  label: string; value: EndpointDraft; fallback?: string | null; orders: OrderLocation[]; onChange: (value: EndpointDraft) => void;
}) {
  const [locating, setLocating] = useState(false), [error, setError] = useState("");
  const request = useRef(0), current = useRef({ label, value, onChange });
  current.current = { label, value, onChange };
  useEffect(() => () => { ++request.current; }, []);
  useEffect(() => { ++request.current; setLocating(false); setError(""); }, [label]);
  const locate = (draft: EndpointDraft) => {
    const id = ++request.current, requestedLabel = label;
    setError("");
    if (!navigator.geolocation) { setError("Current location is unavailable in this browser. Enter coordinates instead."); return; }
    setLocating(true);
    const active = () => request.current === id && current.current.label === requestedLabel && current.current.value.mode === "location";
    navigator.geolocation.getCurrentPosition(position => {
      if (!active()) return;
      setLocating(false);
      try { const point = parseRoutePoint(`${position.coords.latitude},${position.coords.longitude}`);
        current.current.onChange({ ...draft, coordinates: `${point.lat},${point.lng}` });
      } catch { setError("A valid current location could not be read. Retry or enter coordinates."); }
    }, failure => {
      if (!active()) return;
      setLocating(false); setError(failure.code === 1 ? "Location permission was denied. Allow access or enter coordinates."
        : "Could not get your location. Retry or enter coordinates.");
    }, { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 });
  };
  return <div className="route-endpoint">
    <label>{label}</label>
    <Dropdown aria-label={`${label} source`} value={value.mode} options={[
      { label: "Executive default", value: "default" }, { label: "Selected order location", value: "order" },
      { label: "Enter coordinates", value: "coordinates" }, { label: "Current location", value: "location" },
    ]} onChange={e => {
      ++request.current; setLocating(false); setError("");
      const next = { ...value, mode: e.value, coordinates: e.value === "location" ? "" : value.coordinates, orderId: value.orderId || String(orders[0]?.orderId ?? "") };
      current.current.value = next; onChange(next);
      if (next.mode === "location") locate(next);
    }} />
    {value.mode === "default" && <small>{fallback || "No saved coordinates. Choose an order or enter coordinates."}</small>}
    {value.mode === "order" && <Dropdown aria-label={`${label} order`} value={value.orderId} filter
      options={orders.map(o => ({ label: `${o.orderId} — ${o.customerName}`, value: String(o.orderId) }))} onChange={e => onChange({ ...value, orderId: e.value })} />}
    {value.mode === "coordinates" && <InputText aria-label={`${label} coordinates`} placeholder="Latitude, longitude" value={value.coordinates} onChange={e => onChange({ ...value, coordinates: e.target.value })} />}
    {value.mode === "location" && <>
      <Button label={locating ? "Getting location…" : "Use current location"} icon="pi pi-map-marker" size="small" outlined loading={locating} disabled={locating} onClick={() => locate(value)} />
      {value.coordinates && <small>{value.coordinates}</small>}{locating && <small role="status">Waiting for your browser's location…</small>}
      {error && <small role="alert">{error}</small>}
    </>}
  </div>;
}
